import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import endpoints from '../api/endpoints';
import {
  setTokens,
  clearTokens,
  hasStoredRefreshToken,
  registerAuthFailureHandler
} from '../api/client';

// AuthContext — single source of truth for the logged-in user + their /me
// payload (which carries the subscription block). Token storage is handled
// inside api/client.js; this context only mirrors the higher-level "are we
// logged in" state for the React tree.

export const AuthContext = createContext({
  status: 'unknown',           // 'unknown' | 'unauthenticated' | 'authenticated'
  me: null,                    // /me response data
  loginMaster: async () => {},
  loginUser: async () => {},
  logout: async () => {},
  refreshMe: async () => {}
});

function detectLoginEndpoint(email) {
  if (typeof email !== 'string') return null;
  if (email.endsWith('@master.glass-i')) return 'master';
  if (email.endsWith('@user.glass-i')) return 'user';
  return null;
}

export function AuthProvider({ children }) {
  const [status, setStatus] = useState(hasStoredRefreshToken() ? 'unknown' : 'unauthenticated');
  const [me, setMe] = useState(null);

  const refreshMe = useCallback(async () => {
    const data = await endpoints.auth.me();
    setMe(data);
    return data;
  }, []);

  const handleAuthFailure = useCallback(() => {
    setMe(null);
    setStatus('unauthenticated');
  }, []);

  // Wire the axios client's auth-failure callback once.
  useEffect(() => {
    registerAuthFailureHandler(handleAuthFailure);
    return () => registerAuthFailureHandler(null);
  }, [handleAuthFailure]);

  // Boot: if we have a refresh token in sessionStorage, silently refresh +
  // pull /me to rehydrate. The client interceptor handles the 401 path.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hasStoredRefreshToken()) {
        if (!cancelled) setStatus('unauthenticated');
        return;
      }
      try {
        // Touching /me triggers the 401-refresh interceptor when the
        // memory accessToken is empty (it always is on boot).
        const data = await endpoints.auth.me();
        if (cancelled) return;
        setMe(data);
        setStatus('authenticated');
      } catch (_err) {
        if (cancelled) return;
        clearTokens();
        setStatus('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const finishLogin = useCallback(async (loginPayload) => {
    setTokens({
      accessToken: loginPayload.accessToken,
      refreshToken: loginPayload.refreshToken
    });
    const data = await endpoints.auth.me();
    setMe(data);
    setStatus('authenticated');
    return data;
  }, []);

  // Stage 5M: helper that detects the MFA_REQUIRED envelope so the page
  // can switch into a code-entry step instead of treating the login as
  // a hard failure. The backend returns:
  //   { error: { code: 'MFA_REQUIRED', details: { challengeId, expiresInSeconds } } }
  // We normalise to a plain { mfaRequired:true, challengeId, expiresInSeconds }
  // shape that LoginPage can read.
  function extractMfaChallenge(err) {
    const env = err && err.response && err.response.data && err.response.data.error;
    if (!env || env.code !== 'MFA_REQUIRED') return null;
    const details = env.details || {};
    if (!details.challengeId) return null;
    return {
      mfaRequired:      true,
      challengeId:      String(details.challengeId),
      expiresInSeconds: Number(details.expiresInSeconds) || 300
    };
  }

  const loginMaster = useCallback(
    async (email, password) => {
      try {
        const data = await endpoints.auth.masterLogin({ email, password });
        return finishLogin(data);
      } catch (err) {
        const challenge = extractMfaChallenge(err);
        if (challenge) return challenge;
        throw err;
      }
    },
    [finishLogin]
  );

  const loginUser = useCallback(
    async (email, password) => {
      try {
        const data = await endpoints.auth.userLogin({ email, password });
        return finishLogin(data);
      } catch (err) {
        const challenge = extractMfaChallenge(err);
        if (challenge) return challenge;
        throw err;
      }
    },
    [finishLogin]
  );

  const loginByEmail = useCallback(
    async (email, password) => {
      const which = detectLoginEndpoint(email);
      if (which === 'master') return loginMaster(email, password);
      if (which === 'user') return loginUser(email, password);
      const err = new Error(
        'email must end with @master.glass-i or @user.glass-i'
      );
      err.code = 'INVALID_EMAIL_DOMAIN';
      throw err;
    },
    [loginMaster, loginUser]
  );

  // Stage 5M: complete a deferred login by submitting the 6-digit TOTP
  // code from the master's Google Authenticator. The MFA_REQUIRED branch
  // of loginByEmail returns a challenge descriptor; the LoginPage then
  // calls this with the same challengeId + the user-entered code. On
  // success, the response shape is identical to a normal login, so we
  // funnel it through finishLogin.
  const verifyLoginMfa = useCallback(
    async ({ challengeId, code }) => {
      const data = await endpoints.auth.loginMfaVerify({ challengeId, code });
      return finishLogin(data);
    },
    [finishLogin]
  );

  const logout = useCallback(async () => {
    try {
      await endpoints.auth.logout();
    } catch (_err) {
      /* token may already be invalid; clear anyway */
    }
    clearTokens();
    setMe(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo(
    () => ({
      status,
      me,
      loginMaster,
      loginUser,
      loginByEmail,
      verifyLoginMfa,
      logout,
      refreshMe
    }),
    [status, me, loginMaster, loginUser, loginByEmail, verifyLoginMfa, logout, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
